import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Crown, Eye, EyeSlash, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1615657804432-5ee3750d2dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxmb3J0YWxlemElMjBza3lsaW5lfGVufDB8fHx8MTc4MjEwNDEyOHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [municipioId, setMunicipioId] = useState("");
  const [munis, setMunis] = useState([]);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (mode === "register" && munis.length === 0) {
      api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
    }
  }, [mode, munis.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !municipioId) {
      toast.error("Selecione o município de responsabilidade");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password, "interno");
        toast.success("Acesso autorizado");
        nav("/dashboard");
      } else {
        await register(name, email, password, municipioId);
        toast.success("Cadastro enviado para autorização", {
          description: "Você será notificado quando o administrador liberar seu acesso.",
          duration: 8000,
        });
        setMode("login");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao autenticar";
      toast.error(typeof msg === "string" ? msg : "Erro ao autenticar");
    } finally {
      setBusy(false);
    }
  };

  let buttonLabel = mode === "login" ? "Entrar" : "Cadastrar";
  if (busy) buttonLabel = "Processando…";

  return (
    <div className="min-h-screen flex bg-[#0F172A] text-[#E9EEF5]">
      {/* Left: visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img src={BG} alt="Fortaleza" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-[#0F172A]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0F172A]" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="flex items-center gap-3">
            <Crown weight="duotone" size={36} className="text-[#F59E0B]" />
            <div>
              <div className="font-heading text-xl tracking-tight">Soluções Contratuais</div>
              <div className="uppercase-label">Gestão Pública</div>
            </div>
          </div>
          <div>
            <div className="uppercase-label mb-3">Estado do Ceará</div>
            <h1 className="font-heading text-5xl lg:text-6xl tracking-tight leading-tight">
              <span className="gold-text">Soluções Contratuais</span>
              <br /> para gestão pública.
            </h1>
            <p className="text-[#94A3B8] mt-6 max-w-md text-base leading-relaxed">
              Plataforma oficial para fiscalização e gerenciamento de contratos
              celebrados pelos 184 municípios cearenses.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 relative z-10">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <Crown weight="duotone" size={32} className="text-[#F59E0B]" />
            <div className="font-heading text-lg">Soluções Contratuais</div>
          </div>

          <div className="uppercase-label mb-2">
            {mode === "login" ? "Acesso restrito" : "Novo cadastro"}
          </div>
          <h2 className="font-heading text-3xl tracking-tight mb-2">
            {mode === "login" ? "Entrar no sistema" : "Criar conta"}
          </h2>
          <p className="text-[#94A3B8] text-sm mb-10">
            {mode === "login"
              ? "Use suas credenciais para acessar o painel."
              : "Cadastre-se para iniciar a gestão de contratos."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="uppercase-label block mb-2">Nome completo</label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="tce-input w-full px-4 py-3 rounded-md"
                  placeholder="Seu nome"
                />
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="uppercase-label block mb-2 flex items-center gap-1.5">
                  <Buildings size={12} weight="duotone" className="text-[#F59E0B]" />
                  Município de responsabilidade
                </label>
                <select
                  data-testid="register-municipio-input"
                  required
                  value={municipioId}
                  onChange={(e) => setMunicipioId(e.target.value)}
                  className="tce-input w-full px-4 py-3 rounded-md"
                >
                  <option value="">Selecione o município…</option>
                  {munis.map((m) => (
                    <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>
                  ))}
                </select>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Após autorização, você terá acesso apenas aos contratos deste município.
                </p>
              </div>
            )}
            <div>
              <label className="uppercase-label block mb-2">E-mail funcional</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="tce-input w-full px-4 py-3 rounded-md"
                placeholder="seu.email@municipio.ce.gov.br"
              />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Senha</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="tce-input w-full px-4 py-3 rounded-md pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="toggle-password-button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#F59E0B]"
                >
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              disabled={busy}
              className="w-full bg-[#F59E0B] text-[#0F172A] font-medium py-3 rounded-md hover:bg-[#FCD34D] transition-colors disabled:opacity-50"
            >
              {buttonLabel}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#334155] text-sm text-[#94A3B8]">
            {mode === "login" ? "Novo aqui?" : "Já tem conta?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#F59E0B] hover:text-[#FCD34D] font-medium"
            >
              {mode === "login" ? "Criar conta" : "Fazer login"}
            </button>
          </div>
          <div className="mt-4 text-sm text-[#94A3B8]">
            É empresa contratada?{" "}
            <a href="/fornecedor/login" data-testid="link-portal-fornecedor" className="text-[#009C3B] hover:text-[#22C55E] font-medium">
              Acesse o Portal do Fornecedor
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
